<template>
  <v-dialog
    v-model="showDialog"
    width="500"
    :fullscreen="$vuetify.breakpoint.smAndDown"
    :persistent="persistent"
  >
    <template
      v-if="!persistent"
      v-slot:activator="{ on }"
    >
      <v-btn
        depressed
        color="accent"
        :disabled="$store.state.loading"
        :loading="$store.state.loading"
        v-on="on"
      >
        Sign in
      </v-btn>
    </template>

    <v-card class="d-flex flex-column">
      <div
        v-if="!persistent"
        class="d-flex pa-4 pb-0"
      >
        <v-spacer />

        <v-btn
          icon
          @click="showDialog = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-title class="justify-center">
        <v-tooltip right>
          <template #activator="{ on }">
            <v-avatar
              color="primary"
              size="30"
              class="beta-icon"
            >
              <v-icon
                dark
                v-on="on"
              >
                mdi-beta
              </v-icon>
            </v-avatar>
          </template>

          <span>Vjago is currently in Beta, you may encounter issues and bugs</span>
        </v-tooltip>

        {{ title }}
      </v-card-title>

      <v-card-text class="flex-grow-1">
        <v-alert
          v-if="error && error.code === 'auth/account-exists-with-different-credential'"
          type="info"
          prominent
          text
        >
          The email address {{ error.email }} is already associated with a Google account,
          please sign in with Google.
        </v-alert>

        <div>
          <v-btn
            color="#DE5246"
            block
            class="white--text"
            @click="signInWithGoogle()"
          >
            <v-icon left>
              mdi-google
            </v-icon>
            Google
          </v-btn>
        </div>

        <div class="mt-4">
          <v-btn
            color="#4267B2"
            class="white--text"
            block
            :disabled="error && error.code === 'auth/account-exists-with-different-credential'"
            @click="signInWithFacebook()"
          >
            <v-icon left>
              mdi-facebook
            </v-icon>
            Facebook
          </v-btn>
        </div>

        <div class="mt-4">
          <v-btn
            color="#00ACEE"
            class="white--text"
            block
            :disabled="error && error.code === 'auth/account-exists-with-different-credential'"
            @click="signInWithTwitter()"
          >
            <v-icon left>
              mdi-twitter
            </v-icon>
            Twitter
          </v-btn>
        </div>
      </v-card-text>

      <v-divider class="ma-4" />

      <v-card-text>
        By creating an account, you agree to our
        <a
          href="https://vjago.com/terms"
          target="_blank"
        >
          Terms and Conditions
        </a>.
        For more information about Vjago's privacy practices, read our
        <a
          href="https://vjago.com/policy"
          target="_blank"
        >
          Privacy Policy
        </a>.
        We'll occasionally send you important account-related emails.
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import * as firebase from 'firebase/app'

import { analytics, auth } from '@/plugins/firebase'

export default {
  props: {
    persistent: {
      type: Boolean,
      default: false,
      required: false
    },
    title: {
      type: String,
      default: 'Sign in with',
      required: false
    },
    value: {
      type: Boolean,
      required: false
    }
  },
  data () {
    return {
      error: null,
      showDialog: false
    }
  },
  watch: {
    value (show) {
      this.showDialog = show
    },
    showDialog (show) {
      if (show) {
        analytics.logEvent('screen_view', { screen_name: 'signin' })
      }

      if (this.value !== undefined && this.value !== show) {
        this.$emit('input', show)
      }
    }
  },
  mounted () {
    this.showDialog = !!this.persistent || !!this.value

    auth.getRedirectResult()
      .then(credential => {
        if (credential.user) {
          this.onSuccessfulSignIn(credential)
        }
      })
  },
  methods: {
    signInWithFacebook () {
      const provider = new firebase.auth.FacebookAuthProvider()
      provider.setCustomParameters({
        'display': 'popup'
      })

      this.signIn(provider)
    },
    signInWithGoogle () {
      this.signIn(new firebase.auth.GoogleAuthProvider())
    },
    signInWithTwitter () {
      this.signIn(new firebase.auth.TwitterAuthProvider())
    },
    /**
     * @param {firebase.auth.OAuthProvider} provider
     */
    async signIn (provider) {
      try {
        let credential
        if (this.$vuetify.breakpoint.xsOnly) {
          credential = await firebase.auth().signInWithRedirect(provider)
        } else {
          credential = await firebase.auth().signInWithPopup(provider)
        }

        this.onSuccessfulSignIn(credential)
      } catch (e) {
        this.error = e
      }
    },

    /**
     * @param {firebase.auth.UserCredential} credential
     */
    async onSuccessfulSignIn (credential) {
      analytics.logEvent(credential.additionalUserInfo.isNewUser
        ? 'signup' : 'login', { method: credential.additionalUserInfo.providerId })

      await this.$store.dispatch('doFirstLoad')

      if (this.$router.currentRoute.name === 'signin') {
        // if user is on the signin page then redirect
        this.$router.replace({
          path: this.$router.currentRoute.query.redirect || '/'
        })
      } else {
        // otherwise the signin dialog is open over another page
        // so just close it
        this.showDialog = false
      }
    }
  }
}
</script>

<style lang="scss">
.beta-icon {
  position: absolute !important;
  top: 5px;
  left: 5px;
}
</style>
